import { useEffect, useRef, useState } from 'react';
import { ROI_HEIGHT_FRAC, ROI_WIDTH_FRAC, createScanner } from '../lib/barcode';
import { useToast } from './Toast';

// Non-standard MediaStreamTrack capabilities/constraints (zoom/torch/focusMode)
// shipped by Chromium but absent from lib.dom. Typed locally so the
// feature-detection reads cleanly without an `any` cast at each use site.
interface CameraCapabilities {
  zoom?: { min: number; max: number; step?: number };
  torch?: boolean;
  focusMode?: string[];
}
interface CameraSettings {
  zoom?: number;
}
interface AdvancedConstraint {
  zoom?: number;
  torch?: boolean;
  focusMode?: string;
}
interface ZoomState {
  min: number;
  max: number;
  step: number;
  value: number;
}

const TEXT_PERMISSION_DENIED =
  'Necesitas permitir el acceso a la cámara para escanear. Habilítalo en la configuración del navegador.';
const TEXT_CAMERA_BUSY = 'No pudimos acceder a la cámara. Cierra otras apps que la estén usando.';
const TEXT_CHECKSUM_INVALID =
  'Ups, no pudimos validar el código. Acerca la cámara o intenta desde otro ángulo.';
const TEXT_DECODER_FAILED =
  'Hubo un problema iniciando el lector de códigos. Intenta cerrar y reabrir el escáner.';

const TIMEOUT_MS = 30_000;
const CHECKSUM_BANNER_MS = 2_000;
const VIDEO_READY_TIMEOUT_MS = 1_500;

function awaitVideoReady(
  videoEl: HTMLVideoElement,
  hasVideoTrack: boolean,
  signal?: AbortSignal,
): Promise<void> {
  if (!hasVideoTrack) return Promise.resolve();
  return new Promise<void>((resolve) => {
    if (videoEl.readyState >= 2 && videoEl.videoWidth > 0) return resolve();
    const cleanup = (): void => {
      videoEl.removeEventListener('loadedmetadata', onLoaded);
      clearTimeout(safetyTimer);
      signal?.removeEventListener('abort', onAbort);
    };
    const onLoaded = (): void => {
      cleanup();
      resolve();
    };
    const onAbort = (): void => {
      cleanup();
      resolve();
    };
    const safetyTimer = setTimeout(() => {
      cleanup();
      resolve();
    }, VIDEO_READY_TIMEOUT_MS);
    videoEl.addEventListener('loadedmetadata', onLoaded);
    if (signal) {
      if (signal.aborted) {
        cleanup();
        resolve();
        return;
      }
      signal.addEventListener('abort', onAbort);
    }
  });
}

function mapAcquireError(e: unknown): { text: string; closeModal: boolean } {
  const name = (e as { name?: string }).name;
  if (name === 'NotAllowedError') {
    return { text: TEXT_PERMISSION_DENIED, closeModal: true };
  }
  return { text: TEXT_CAMERA_BUSY, closeModal: false };
}

/**
 * Best-effort camera-control feature detection (§2). Reads the live track's
 * capabilities and reports which controls to surface. Every field is gated on
 * the device actually reporting the capability — on iOS Safari most return
 * absent/false, so nothing renders and nothing throws.
 */
function detectCameraControls(track: MediaStreamTrack): {
  zoom: ZoomState | null;
  torchSupported: boolean;
  continuousFocus: boolean;
} {
  let caps: CameraCapabilities = {};
  try {
    caps = (track.getCapabilities?.() ?? {}) as CameraCapabilities;
  } catch {
    caps = {};
  }
  let zoom: ZoomState | null = null;
  if (caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > caps.zoom.min) {
    let current = caps.zoom.min;
    try {
      const settings = (track.getSettings?.() ?? {}) as CameraSettings;
      if (typeof settings.zoom === 'number') current = settings.zoom;
    } catch {
      // keep min as the starting value
    }
    zoom = { min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step ?? 0.1, value: current };
  }
  const torchSupported = caps.torch === true;
  const continuousFocus = Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous');
  return { zoom, torchSupported, continuousFocus };
}

interface LiveViewfinderProps {
  onClose: () => void;
  onDecoded: (ean13: string) => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

/**
 * Live viewfinder + continuous decode loop — the Chrome/Chromium path
 * (healthy native `BarcodeDetector`). Held a live `getUserMedia` stream and
 * ticks `detect()` on a cropped ROI each frame. iOS Safari/WebKit does NOT take
 * this path (see {@link BarcodeScanner} dispatcher / issue #167) — it would OOM
 * the tab; it uses the tap-to-capture still fallback instead.
 */
export function LiveViewfinder({ onClose, onDecoded, triggerRef }: LiveViewfinderProps): React.ReactElement {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const scannerRef = useRef<{ stop: () => void } | null>(null);
  const checksumTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onDecodedRef = useRef(onDecoded);
  onDecodedRef.current = onDecoded;
  const triggerRefStable = useRef(triggerRef);
  triggerRefStable.current = triggerRef;

  const { show: showToast } = useToast();
  const [showChecksumBanner, setShowChecksumBanner] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  const [decoded, setDecoded] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // Camera controls — populated only when the live track reports the
  // capability (§2). Absent capability ⇒ the control never renders.
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // Apply one advanced track constraint, swallowing rejections: zoom/torch are
  // best-effort and some devices reject mid-stream constraint changes.
  const applyTrackConstraint = (c: AdvancedConstraint): void => {
    const track = trackRef.current;
    if (!track) return;
    void Promise.resolve(track.applyConstraints({ advanced: [c] } as unknown as MediaTrackConstraints)).catch(
      () => {},
    );
  };

  const onZoomInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = Number(e.target.value);
    setZoom((z) => (z ? { ...z, value } : z));
    applyTrackConstraint({ zoom: value });
  };

  const toggleTorch = (): void => {
    setTorchOn((prev) => {
      const next = !prev;
      applyTrackConstraint({ torch: next });
      return next;
    });
  };

  const close = (): void => {
    const trig = triggerRefStable.current?.current;
    if (trig && typeof trig.focus === 'function') trig.focus();
    onCloseRef.current();
  };

  // Initial focus.
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Esc to close.
  // biome-ignore lint/correctness/useExhaustiveDependencies: close uses refs only and is stable
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Camera + scanner lifecycle. Re-runs on retryNonce.
  // biome-ignore lint/correctness/useExhaustiveDependencies: retryNonce drives re-mount; close/showToast are stable
  useEffect(() => {
    let cancelled = false;
    let fired = false;
    const abortCtrl = new AbortController();
    setTimedOut(false);
    setShowChecksumBanner(false);
    setDecoded(false);
    setZoom(null);
    setTorchSupported(false);
    setTorchOn(false);
    trackRef.current = null;

    const md = navigator.mediaDevices;
    if (!md) return;

    // Idempotent teardown: stop scanner, stop tracks for the captured stream
    // and any later-bound streamRef, and clear timers. Safe to call from any
    // cancellation point in the async chain or the effect cleanup.
    const teardown = (stream?: MediaStream | null): void => {
      scannerRef.current?.stop();
      scannerRef.current = null;
      if (checksumTimerRef.current) clearTimeout(checksumTimerRef.current);
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
      checksumTimerRef.current = null;
      timeoutTimerRef.current = null;
      stream?.getTracks().forEach((t) => t.stop());
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };

    const bailIfCancelled = (s: MediaStream): boolean => {
      if (cancelled) {
        teardown(s);
        return true;
      }
      return false;
    };

    // Request HD on the rear camera. Safari iOS defaults to 640×480 to save
    // battery, which is below the Nyquist limit for EAN-13's 95 modules
    // when the code isn't framed perfectly — ZXing scans a mush of aliased
    // bars and returns no results. `ideal` is a soft constraint, so devices
    // that can't deliver 1080p just give us the closest mode.
    md.getUserMedia({
      video: {
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    })
      .then(async (stream) => {
        if (bailIfCancelled(stream)) return;
        streamRef.current = stream;
        const videoEl = videoRef.current;
        if (!videoEl) return;
        videoEl.srcObject = stream;
        // Wait for metadata + first frame before starting the decoder.
        // Safari resolves play() before videoWidth/videoHeight populate, so
        // ZXing's internal canvas would be 0×0 on the first many ticks and
        // never produce a result. Awaiting loadedmetadata + a paint avoids it.
        // Skip in test envs whose mock stream has no real video track —
        // jsdom never fires loadedmetadata. 1.5s safety timeout for degraded
        // real environments; on Safari the event fires within ~50–200ms.
        const hasVideoTrack = stream.getTracks().some((t) => t.kind === 'video');
        await awaitVideoReady(videoEl, hasVideoTrack, abortCtrl.signal);
        if (bailIfCancelled(stream)) return;
        try {
          await videoEl.play();
        } catch {
          // ignore — autoplay restrictions; play() may reject but the stream
          // can still surface frames.
        }
        if (bailIfCancelled(stream)) return;
        setStreamReady(true);

        // §2 best-effort camera controls. Read capabilities off the live video
        // track; surface only what the device reports. Continuous autofocus is
        // applied immediately (helps lock onto small/curved codes); zoom/torch
        // render conditional controls wired to applyConstraints.
        const videoTrack = stream.getVideoTracks?.()?.[0] ?? null;
        trackRef.current = videoTrack;
        if (videoTrack) {
          const ctrls = detectCameraControls(videoTrack);
          if (ctrls.continuousFocus) applyTrackConstraint({ focusMode: 'continuous' });
          setZoom(ctrls.zoom);
          setTorchSupported(ctrls.torchSupported);
        }

        scannerRef.current = createScanner({
          videoEl: videoRef.current as HTMLVideoElement,
          stream,
          onValid: (ean13) => {
            // Decoder loop can fire onValid more than once before stop()
            // propagates; guard against duplicate onDecoded calls.
            if (cancelled || fired) return;
            fired = true;
            if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
            timeoutTimerRef.current = null;
            scannerRef.current?.stop();
            scannerRef.current = null;
            setDecoded(true);
            if (typeof navigator.vibrate === 'function') navigator.vibrate(50);
            // AC-15.10 (decisión 2026-05-06, opción b): si onDecoded rechaza
            // por un 5xx en /search/suggest, NO tearremos el stream ni
            // cerramos el modal — el parent muestra el toast y el modal
            // queda en "dead preview" (decoder detenido, video aún vivo)
            // hasta que el usuario cierre con Esc/✕/backdrop. Cerrar acá
            // mataría la cámara recién adquirida y forzaría re-acquire
            // (~1 s en iOS) + posible re-prompt de permiso.
            Promise.resolve(onDecodedRef.current(ean13)).catch((err) => {
              if (!cancelled) console.warn('[scanner] onDecoded failed:', err);
            });
          },
          onInvalid: (reason) => {
            if (cancelled || fired) return;
            if (reason !== 'checksum') return;
            setShowChecksumBanner(true);
            if (checksumTimerRef.current) clearTimeout(checksumTimerRef.current);
            checksumTimerRef.current = setTimeout(() => {
              setShowChecksumBanner(false);
            }, CHECKSUM_BANNER_MS);
          },
          onError: (err) => {
            console.warn('barcode scanner error', err);
            showToast(TEXT_DECODER_FAILED, 'error');
          },
        });
        if (bailIfCancelled(stream)) return;

        timeoutTimerRef.current = setTimeout(() => {
          fired = true;
          setTimedOut(true);
          teardown(stream);
        }, TIMEOUT_MS);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const mapped = mapAcquireError(e);
        showToast(mapped.text, 'error');
        if (mapped.closeModal) close();
      });

    return () => {
      cancelled = true;
      abortCtrl.abort();
      teardown();
      setStreamReady(false);
    };
  }, [retryNonce]);

  // Manual focus trap: keep tab order inside the dialog.
  const onKeyDownTrap = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) close();
  };

  // Reticle geometry is the ROI geometry — share the exact fractions so the
  // visible aim box and the decoded crop never drift (§1/§3).
  const reticleStyle: React.CSSProperties = {
    width: `${ROI_WIDTH_FRAC * 100}%`,
    height: `${ROI_HEIGHT_FRAC * 100}%`,
  };
  const reticleClass = decoded ? 'is-success' : showChecksumBanner ? 'is-error' : '';

  return (
    <div data-testid="scan-backdrop" onClick={onBackdropClick} className="scan-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Escanear código de barras"
        onKeyDown={onKeyDownTrap}
        className="scan-modal"
      >
        {/* Live feed — always mounted so overlays never reflow it. */}
        <video ref={videoRef} autoPlay playsInline muted className="scan-video" />

        {/* Skeleton shimmer until the first frame paints. */}
        {!streamReady && !timedOut && <div className="scan-skeleton" aria-hidden="true" />}

        {/* Reticle + scrim aligned to the ROI. */}
        {!timedOut && (
          <div className={`scan-reticle ${reticleClass}`} style={reticleStyle} aria-hidden="true">
            <span className="scan-bracket tl" />
            <span className="scan-bracket tr" />
            <span className="scan-bracket bl" />
            <span className="scan-bracket br" />
            {streamReady && !decoded && <span className="scan-line" />}
          </div>
        )}

        {/* Close ✕ (hidden during timeout — its panel offers Cerrar). */}
        {!timedOut && (
          <button
            type="button"
            ref={closeBtnRef}
            aria-label="Cerrar"
            onClick={close}
            className="scan-pill scan-close"
          >
            ✕
          </button>
        )}

        {/* Floating camera controls — only what the device reports. */}
        {!timedOut && streamReady && (torchSupported || zoom) && (
          <div className="scan-controls">
            {torchSupported && (
              <button
                type="button"
                className="scan-pill scan-ctl"
                aria-label="Linterna"
                aria-pressed={torchOn}
                onClick={toggleTorch}
              >
                🔦
              </button>
            )}
            {zoom && (
              <label className="scan-pill scan-zoom">
                <span aria-hidden="true">🔍</span>
                <input
                  type="range"
                  aria-label="Zoom"
                  min={zoom.min}
                  max={zoom.max}
                  step={zoom.step}
                  value={zoom.value}
                  onChange={onZoomInput}
                />
              </label>
            )}
          </div>
        )}

        {/* Status / hint overlays — positioned, never reflow the feed. */}
        {!streamReady && !timedOut && <div className="scan-status">Iniciando cámara…</div>}
        {showChecksumBanner && !timedOut && (
          <p role="status" className="scan-status scan-status-error">
            {TEXT_CHECKSUM_INVALID}
          </p>
        )}

        {/* Timeout panel — centered sheet over the frozen feed. */}
        {timedOut && (
          <div className="scan-timeout">
            <p className="mb-4 text-text-primary">No detectamos ningún código en 30 segundos.</p>
            <div className="flex gap-2 justify-center">
              <button type="button" className="btn btn-primary" onClick={() => setRetryNonce((n) => n + 1)}>
                Reintentar escaneo
              </button>
              <button type="button" className="btn btn-secondary" onClick={close}>
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
